import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { tablet } from '@shared/ui/atoms/MediaQueries';
import Colours from '@shared/ui/atoms/Colours';
import { getParameterByName } from '@shared/utils/UrlUtils';
import { SidebarItem } from '../office-management-page-v2/components/SidebarItem';
import Layout from '@shared/ui/organisms/layout/Layout';

// Shared chrome for the settings pages that use a left-hand section menu
// (company settings, edit user, personal preferences): a white ribbon with a
// back button and title, then a sidebar of sections and one panel at a time.

export interface SettingsMenuItem<T> {
  view: T;
  icon: IconTypes;
  label: string;
}

interface RibbonProps {
  title: string;
  subtitle?: ReactNode;
  backUrl?: string;
  onBack?: () => void;
}

// Full-width page frame for these settings pages. Layout's fullWidth caps the
// content at 2000px; lifting the cap lets the white ribbon run edge to edge on
// wide screens, while the sidebar and panels stay centred at 1400px.
const UNCAPPED_WIDTH = { maxWidth: 'none' };

export function SettingsPageLayout(props: { children: ReactNode }) {
  return (
    <Layout fullWidth={true} hideFooter={true} style={UNCAPPED_WIDTH}>
      {props.children}
    </Layout>
  );
}

export function SettingsRibbon(props: RibbonProps) {
  const { t } = useTranslation();
  return (
    <RibbonContainer>
      <RibbonContentContainer>
        <GoBackButton url={props.backUrl} onClick={props.onBack} style={{marginBottom: 16}} />
        <TitleRow>
          <HeadlineMedium>{t(props.title)}</HeadlineMedium>
          {props.subtitle}
        </TitleRow>
      </RibbonContentContainer>
    </RibbonContainer>
  );
}

interface SidebarLayoutProps<T> {
  menuItems: SettingsMenuItem<T>[];
  activeView: T;
  onViewChange: (view: T) => void;
  children: ReactNode;
}

export function SettingsSidebarLayout<T extends string | number>(props: SidebarLayoutProps<T>) {
  const { t } = useTranslation();
  const { menuItems, activeView, onViewChange } = props;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const activeItem = menuItems.find(item => item.view === activeView) ?? menuItems[0];

  // Panels can switch the view themselves (e.g. a "manage" link), so close the
  // mobile menu whenever the view changes, not only on a sidebar click.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [activeView]);

  const onItemClick = (view: T) => {
    setMobileMenuOpen(false);
    onViewChange(view);
  };

  return (
    <ContentContainer>
      <Sidebar>
        {activeItem && (
          <MobileMenuToggle onClick={() => setMobileMenuOpen(open => !open)}
                            aria-expanded={mobileMenuOpen}
                            type="button">
            <MobileMenuToggleLabel>
              <Icon icon={activeItem.icon} />
              <BodyRegular weight={600}>{t(activeItem.label)}</BodyRegular>
            </MobileMenuToggleLabel>
            <Icon icon={mobileMenuOpen ? IconTypes.Up : IconTypes.Down} />
          </MobileMenuToggle>
        )}
        <SidebarItems $open={mobileMenuOpen}>
          {menuItems.map(item => (
            <SidebarItem key={String(item.view)}
                         icon={item.icon}
                         label={item.label}
                         active={activeView === item.view}
                         onClick={() => onItemClick(item.view)} />
          ))}
        </SidebarItems>
      </Sidebar>
      <MainContent>
        {props.children}
      </MainContent>
    </ContentContainer>
  );
}

// Holds the active panel for pages that keep it in local state. The panel is
// mirrored to ?panel= so a refresh or shared link reopens it; replaceState keeps
// panel switches out of the browser history, so Back still leaves the page.
// A requested panel that is not in `views` (hidden for this user) falls back to
// the first one.
export function useSettingsPanel<T extends string>(views: T[]): [T, (view: T) => void] {
  const [requested, setRequested] = useState<string | null>(() => getParameterByName('panel', window.location.href));
  const active = views.find(view => view === requested) ?? views[0];

  const setActive = useCallback((view: T) => {
    setRequested(view);
    const url = new URL(window.location.href);
    url.searchParams.set('panel', view);
    window.history.replaceState(window.history.state, '', url.toString());
  }, []);

  return [active, setActive];
}

// Centred body for the states shown before the sidebar can render
// (loading, failed to load, access required).
export const SettingsMessageContainer = styled.div`
  max-width: 710px;
  margin: 32px auto 0 auto;
  padding: 0 16px;
`;

const Sidebar = styled.div`
  width: 100%;
  @media (${tablet}) {
    width: 220px;
    min-width: 220px;
    max-width: 220px;
    margin: 0;
  }
`

const MobileMenuToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 16px;
  background: white;
  border: none;
  border-bottom: 1px solid ${Colours.lightGrey};
  cursor: pointer;
  i {
    margin-right: 10px;
  }
  @media (${tablet}) {
    display: none;
  }
`

const MobileMenuToggleLabel = styled.div`
  display: flex;
  align-items: center;
`

const SidebarItems = styled.div<{ $open: boolean }>`
  display: ${props => props.$open ? 'block' : 'none'};
  padding: 16px 16px 12px 16px;
  @media (${tablet}) {
    display: block;
    padding: 0;
  }
`

const MainContent = styled.div`
  flex: 1;
  min-height: 500px;
  background-color: white;
  padding: 32px 16px 0 16px;
  @media (${tablet}) {
    padding: 48px 120px;
  }
`

const ContentContainer = styled.div`
  max-width: 1400px;
  background-color: ${Colours.veryLightGrey};
  @media (${tablet}) {
    padding: 0 16px;
    background-color: unset;
    display: flex;
    margin: 24px auto 0 auto;
  }
`

const RibbonContainer = styled.div`
  background-color: white;
  padding: 0 16px;
  box-shadow: 0 9px 22px 0 rgba(46, 74, 148, 0.08);
`

// Title and any subtitle (e.g. the user being edited) share one baseline,
// wrapping onto a second line on narrow screens.
const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 16px;
  row-gap: 4px;
  h3 {
    margin: 0;
  }
`

// ContentContainer is 1400px wide with 16px side padding, so the sidebar starts
// 16px in; 1368px here puts the back button and title on the sidebar's left edge.
const RibbonContentContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  max-width: 1400px;
  padding: 16px 0 20px 0;
  @media (${tablet}) {
    max-width: 1368px;
  }
`
